import { httpClient } from 'utils/http';
import { Vue, Component, Ref } from 'vue-property-decorator';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import {
	CellComponent, CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator,
} from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import mitt from 'mitt';
import Swal from 'sweetalert2';
import { TabEvent } from 'interfaces/app';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.settingsModel>>();
@Component({
	components: {
		TabulatorBtn,
	},
})
export default class AppSettingTable extends Vue.extend(Template) {
	@Ref('addappsetting-modal') private readonly modal!: BModal;

	@Ref('appSettingTable')
	private readonly tableReference!: HTMLDivElement;

	private isLoading = false;

	private visible = false;

	private index = 0;

	private settingsData = {} as Pick<DB.settingsModel, 'key' | 'value'>;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private affiliateid: DB.AffiliateModel['id'] | null = null;

	private AffiliateData: DB.AffiliateModel[] = [];

	private get loggedIn(): boolean {
		return this.$auth.isAuthenticated;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'key',
				title: 'Name',
			},
			{
				field: 'value',
				title: 'Value',
				editor: 'input',
				cellEdited: this.cellValueChanged,
			},
			{
				title: 'action',
				formatter: (cell: CellComponentExtended<DB.settingsModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					instance.$mount();

					return (instance.$el as HTMLElement);
				},
			},
		];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteAppSettingItem,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeDestroy(): void {
		eventBus.off(
			'deleteItem',
			this.deleteAppSettingItem,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private getData(): void {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		const settingsParameter = new URLSearchParams({
			limit: '0',
		});
		if (this.affiliateid) {
			settingsParameter.append(
				'where',
				JSON.stringify({ affiliateid: this.affiliateid }),
			);
		}
		this.table?.alert('Loading');
		Promise.all([
			httpClient.get<DB.AffiliateModel[]>(`/api/affiliate?${parameter}`),
			httpClient.get<DB.settingsModel[]>(`/api/setting?${settingsParameter}`),
		])
			.then(([AffiliateResponse, settingResponse]) => {
				this.AffiliateData = AffiliateResponse.data;
				this.table?.setData(settingResponse.data);
				return undefined;
			}).finally(() => {
				this.table?.clearAlert();
			})
			.catch((err: any) => {
				this.$bvToast.toast(
					`${err.message}`,
					{
						solid: true,
						variant: 'danger',
					},
				);
			});
	}

	private cellValueChanged(cell: CellComponent): void {
		this.table?.alert('Loading');
		const rowData = cell.getData() as DB.settingsModel;
		httpClient.put<DB.settingsModel>(
			`/api/setting/${rowData.id}`,
			{
				value: cell.getValue(),
			},
		).then(() => {
			this.$bvToast.toast(
				`${rowData.key} has been updated`,
				{
					solid: true,
					variant: 'success',
				},
			);
			return undefined;
		}).finally(() => {
			this.table?.clearAlert();
		})
			.catch((error) => {
				this.$bvToast.toast(
					`${error.message}`,
					{
						solid: true,
						variant: 'danger',
					},
				);
			});
	}

	protected saveSetting(): void {
		this.table?.alert('Loading');
		httpClient.post<DB.settingsModel[]>(
			'/api/setting',
			{
				...this.settingsData,
				affiliateid: this.affiliateid,
			},
		).then((res) => {
			this.modal.hide();
			// add data to the ag-grid
			this.table?.addData(res.data);
			this.$bvToast.toast(
				`${this.settingsData.key} has been added`,
				{
					solid: true,
					variant: 'success',
				},
			);
			return undefined;
		}).finally(() => {
			this.table?.clearAlert();
		}).catch((error) => {
			this.$bvToast.toast(
				`${error.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		});
	}

	private deleteAppSettingItem(data: TabEvent<DB.settingsModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading');
				// eslint-disable-next-line promise/no-nesting
				httpClient.delete(`/api/setting/${data.id}`).then(() => {
					this.$bvToast.toast(
						'Item Deleted',
						{
							solid: true,
							variant: 'success',
						},
					);
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$bvToast.toast(
						`${err.message}`,
						{
							solid: true,
							variant: 'danger',
						},
					);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		});
	}
}
